/**
 * Inside the Trace App Clip, WebXR comes from the native host: install
 * `navigator.xr` over it before the app renders, so `XRManager` runs its
 * ordinary WebXR path. Everywhere else this does nothing.
 */

import { PROTOCOL_VERSION, withTimeout, type HelloReply, type NativeHost, type NativeMessage, type PageAck, type PageRequest } from './protocol';
import { RigidTransform } from './rigid';
import { NativeXRSystem, NativeXRWebGLLayer } from './session';

interface MessageHandler { postMessage(msg: unknown): unknown }
interface NativeWindow {
    webkit?: { messageHandlers?: { traceXR?: MessageHandler; traceXRAck?: MessageHandler } };
    __traceXR?: { receive(msg: NativeMessage): void };
}

const HELLO_TIMEOUT_MS = 3000;

function webkitHost(request: MessageHandler, ack: MessageHandler | undefined): NativeHost {
    return {
        request: (msg: PageRequest) => Promise.resolve(request.postMessage(msg)),
        ack: (msg: PageAck) => { ack?.postMessage(msg); },
    };
}

/** Resolves true when `navigator.xr` now comes from the native host. */
export async function installNativeXR(): Promise<boolean> {
    const win = window as Window & NativeWindow;
    const handlers = win.webkit?.messageHandlers;
    if (!handlers?.traceXR || 'xr' in navigator) return false;
    const host = webkitHost(handlers.traceXR, handlers.traceXRAck);
    let hello: HelloReply;
    try {
        hello = await withTimeout(host.request({ type: 'hello', v: PROTOCOL_VERSION }) as Promise<HelloReply>, HELLO_TIMEOUT_MS);
    } catch (error) {
        console.warn('[nativeXR] no hello from the native host:', error);
        return false;
    }
    if (typeof hello !== 'object' || hello === null || hello.v !== PROTOCOL_VERSION) {
        console.warn('[nativeXR] native protocol is not supported; AR is unavailable.', hello);
        return false;
    }
    const system = new NativeXRSystem(host, hello);
    win.__traceXR = { receive: msg => system._receive(msg) };
    Object.defineProperty(navigator, 'xr', { value: system, configurable: true });
    Object.assign(win, { XRWebGLLayer: NativeXRWebGLLayer, XRRigidTransform: RigidTransform });
    return true;
}
